import type {DiscriminatedUnion} from '#lit/types/union.js';
import {createDiscriminator} from '#lit/utils/discriminator.js';
import type {Either} from '#lit/types/Either.js';
import {isEither, left, right} from '#lit/types/Either.js';
import type {VariantFn} from '#lit/types/architecture.js';
import type {FetcherParams} from '#lit/utils/http.js';
import {fetcher} from '#lit/utils/http.js';
import type {IImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';

export type HTTPResponse<Data = unknown> = DiscriminatedUnion<
  'kind',
  {
    pending: void;
    complete: {response: Either<Error, Data>};
  }
>;

/**
 * Simple mapper for creating HTTP discriminators more easily.
 */
export function HTTPResponseDiscriminator<Data, ModelType>() {
  return createDiscriminator<HTTPResponse<Data>, ModelType>()('kind');
}

/**
 * Hooks up an immutable Msg variant to a fetcher.
 */
export function immutableFetcher<ResponseType = unknown, K = unknown>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  host: IImmutableComponent<any, any>,
  params: FetcherParams<K> & {
    url: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handler: VariantFn<any>;
  }
): Promise<void> {
  return wrapPromise<ResponseType>(host, {
    handler: params.handler,
    promise: fetcher<ResponseType>(params.url, {
      ...params,
    }),
  });
}

/**
 * Wraps an existing promise in immutable component calls to work with HTTPResponses.
 */
export async function wrapPromise<ResponseType = unknown, ErrorType = unknown>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  host: IImmutableComponent<any, any>,
  params: {
    promise: Promise<ResponseType | Either<ErrorType, ResponseType>>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handler: VariantFn<any>;
  }
): Promise<void> {
  host.handleUpdate(params.handler({kind: 'pending'}));

  try {
    const response = await params.promise;
    if (isEither(response)) {
      return host.handleUpdate(
        params.handler({
          kind: 'complete',
          response: response,
        })
      );
    }

    return host.handleUpdate(
      params.handler({
        kind: 'complete',
        response: right(response),
      })
    );
  } catch (err) {
    host.handleUpdate(
      params.handler({
        kind: 'complete',
        response: left(err),
      })
    );
  }
}
